import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import './DashboardPage.css';
import { formatCurrency } from '../utils/formatCurrency';
import { DATE_INPUT_MIN, DATE_INPUT_MAX } from '../utils/dateInputBounds';

const API_URL = process.env.REACT_APP_API_URL || '';

function formatMonth(month) {
  const [y, m] = month.split('-');
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

const STATUS_COLORS = {
  paid: '#2E9B6B',
  denied: '#C53030',
  patient: '#C4973B',
  submitted: '#2D4470',
  appealing: '#5B8A9A',
  unsubmitted: '#718096',
};

function statusColor(status) {
  const map = {
    paid: '#2E9B6B', partial: '#2E9B6B',
    denied: '#C53030',
    patient: '#C4973B',
    submitted: '#2D4470', accepted: '#2D4470',
    appealed: '#5B8A9A',
    draft: '#718096', rejected: '#C53030',
  };
  return map[status] || '#718096';
}

function CurrencyTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="dashboard-tooltip">
      <div className="tooltip-label">{label}</div>
      {payload.map((entry, i) => (
        <div key={i} className="tooltip-row">
          <span className="tooltip-dot" style={{ background: entry.color }} />
          <span className="tooltip-name">{entry.name}</span>
          <span className="tooltip-value">{formatCurrency(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

function CountTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="dashboard-tooltip">
      <div className="tooltip-label">{label}</div>
      {payload.map((entry, i) => (
        <div key={i} className="tooltip-row">
          <span className="tooltip-dot" style={{ background: entry.color }} />
          <span className="tooltip-name">{entry.name}</span>
          <span className="tooltip-value">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

function DashboardPage({ selectedPractice, isAdmin }) {
  const practiceId = selectedPractice?.id;

  // Default to last 6 months
  const today = new Date();
  const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 6, 1);
  const defaultStart = sixMonthsAgo.toISOString().split('T')[0];
  const defaultEnd = today.toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // "Last refreshed at" reflects the newest async import. Refetch it on mount / practice change so
  // it isn't stale after an upload (App-level selectedPractice isn't refreshed post-import).
  const [lastImportedAt, setLastImportedAt] = useState(selectedPractice?.last_imported_at || null);

  // Payer tab state
  const [activeTab, setActiveTab] = useState('general');
  const [payers, setPayers] = useState([]);
  const [selectedPayer, setSelectedPayer] = useState('');
  const [payerData, setPayerData] = useState(null);
  const [payerLoading, setPayerLoading] = useState(false);
  const [payerError, setPayerError] = useState(null);

  // Patient tab state
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [patientData, setPatientData] = useState(null);
  const [patientLoading, setPatientLoading] = useState(false);
  const [patientError, setPatientError] = useState(null);

  // Refresh "last refreshed at" on mount / practice change (the page remounts when navigated to
  // from the sidebar), so a just-finished import shows without an app reload.
  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    // Reset immediately so a practice switch never flashes the previous practice's timestamp; the
    // render falls back to the new practice's own selectedPractice.last_imported_at until this resolves.
    setLastImportedAt(null);
    fetch(`${API_URL}/api/practices/${practiceId}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && !cancelled) setLastImportedAt(d.last_imported_at || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [practiceId]);

  const fetchDashboard = useCallback(async () => {
    if (!practiceId || !startDate || !endDate || activeTab !== 'general') return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/dashboard?${params}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const json = await response.json();
        setData(json);
      } else {
        const json = await response.json().catch(() => ({}));
        setError(json.error || 'Failed to load dashboard data');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [practiceId, startDate, endDate, activeTab]);

  // Fetch payer list when practice changes or when switching to payer tab with empty list
  const fetchPayers = useCallback(() => {
    // Admin-only tab, admin-only endpoint — don't spend a 403 on every other user's mount.
    if (!practiceId || !isAdmin) return;
    fetch(`${API_URL}/api/practices/${practiceId}/claims/dashboard/payers`, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.json();
      })
      .then(json => setPayers(json.payers || []))
      .catch(() => setPayers([]));
  }, [practiceId, isAdmin]);

  useEffect(() => { fetchPayers(); }, [fetchPayers]);

  // Retry payer fetch when switching to payer tab if list is empty
  useEffect(() => {
    if (activeTab === 'payer' && payers.length === 0) fetchPayers();
  }, [activeTab, payers.length, fetchPayers]);

  // Fetch payer-filtered dashboard data
  const fetchPayerDashboard = useCallback(async () => {
    if (!practiceId || !selectedPayer || !startDate || !endDate || activeTab !== 'payer') return;
    setPayerLoading(true);
    setPayerError(null);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        payer_name: selectedPayer,
      });
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/dashboard?${params}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const json = await response.json();
        setPayerData(json);
      } else {
        const json = await response.json().catch(() => ({}));
        setPayerError(json.error || 'Failed to load payer dashboard data');
      }
    } catch (err) {
      setPayerError(err.message);
    } finally {
      setPayerLoading(false);
    }
  }, [practiceId, selectedPayer, startDate, endDate, activeTab]);

  // Fetch patient list when practice changes
  const fetchPatients = useCallback(() => {
    // Only the admin-only Patient tab consumes this list.
    if (!practiceId || !isAdmin) return;
    fetch(`${API_URL}/api/practices/${practiceId}/patients`, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.json();
      })
      .then(json => setPatients(json.patients || []))
      .catch(() => setPatients([]));
  }, [practiceId, isAdmin]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  // Retry patient fetch when switching to patient tab if list is empty
  useEffect(() => {
    if (activeTab === 'patient' && patients.length === 0) fetchPatients();
  }, [activeTab, patients.length, fetchPatients]);

  // Fetch patient summary
  const fetchPatientSummary = useCallback(async () => {
    if (!practiceId || !selectedPatient || activeTab !== 'patient') return;
    setPatientLoading(true);
    setPatientError(null);
    try {
      const params = new URLSearchParams({ patient_id: selectedPatient });
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/dashboard/patient-summary?${params}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const json = await response.json();
        setPatientData(json);
      } else {
        const json = await response.json().catch(() => ({}));
        setPatientError(json.error || 'Failed to load patient data');
      }
    } catch (err) {
      setPatientError(err.message);
    } finally {
      setPatientLoading(false);
    }
  }, [practiceId, selectedPatient, activeTab]);

  // Reset dates to default when practice changes
  useEffect(() => {
    const now = new Date();
    const sixAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);
    setStartDate(sixAgo.toISOString().split('T')[0]);
    setEndDate(now.toISOString().split('T')[0]);
    setActiveTab('general');
    setSelectedPayer('');
    setPayerData(null);
    setPayerError(null);
    setSelectedPatient('');
    setPatientData(null);
    setPatientError(null);
  }, [practiceId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    fetchPayerDashboard();
  }, [fetchPayerDashboard]);

  useEffect(() => {
    fetchPatientSummary();
  }, [fetchPatientSummary]);

  if (!selectedPractice) return null;

  // Pick active dataset based on tab (general/payer only — patient has its own render)
  const isPayerView = activeTab === 'payer' && selectedPayer;
  const activeLoading = isPayerView ? payerLoading : loading;
  const activeData = isPayerView ? payerData : data;
  const activeError = isPayerView ? payerError : error;
  const clearActiveError = isPayerView ? () => setPayerError(null) : () => setError(null);

  // Format month labels for charts — add total for stacked bar label
  const collectedData = (activeData?.collected_payment || []).map(d => ({
    ...d,
    month: formatMonth(d.month),
  }));
  const paymentPerClaimData = (activeData?.payment_per_claim || []).map(d => ({
    ...d, month: formatMonth(d.month),
  }));
  const claimsStatusData = (activeData?.claims_status || []).map(d => ({
    ...d,
    month: formatMonth(d.month),
    total: (d.paid || 0) + (d.denied || 0) + (d.patient || 0) + (d.submitted || 0) + (d.appealing || 0) + (d.unsubmitted || 0),
  }));
  const arData = (activeData?.accounts_receivable || []).map(d => ({
    ...d,
    total: (d.insurer_owed || 0) + (d.patient_owed || 0),
  }));

  // Patient tab chart data
  const patientClaimsStatusData = (patientData?.claims_status || []).map(d => ({
    ...d,
    month: formatMonth(d.month),
    total: (d.paid || 0) + (d.denied || 0) + (d.patient || 0) + (d.submitted || 0) + (d.appealing || 0) + (d.unsubmitted || 0),
  }));

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div className="dashboard-title-block">
          <h1 className="page-title">Dashboard</h1>
          {(lastImportedAt || selectedPractice?.last_imported_at) && (
            <div className="dashboard-last-import">
              Last refreshed at{' '}
              {new Date(lastImportedAt || selectedPractice.last_imported_at).toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </div>
          )}
        </div>
        {activeTab !== 'patient' && (
          <div className="dashboard-date-range">
            <label>From</label>
            <input
              type="date"
              min={DATE_INPUT_MIN}
              max={DATE_INPUT_MAX}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <label>To</label>
            <input
              type="date"
              min={DATE_INPUT_MIN}
              max={DATE_INPUT_MAX}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        )}
      </div>

      <div className="dashboard-tabs">
        <button
          className={`tab-button${activeTab === 'general' ? ' active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General
        </button>
        {/* Payer and Patient are mid-revision — admin-only until that lands. The routes
            behind them carry role_required("admin"), so this is presentation only. */}
        {isAdmin && (
          <>
            <button
              className={`tab-button${activeTab === 'payer' ? ' active' : ''}`}
              onClick={() => setActiveTab('payer')}
            >
              Payer
            </button>
            <button
              className={`tab-button${activeTab === 'patient' ? ' active' : ''}`}
              onClick={() => setActiveTab('patient')}
            >
              Patient
            </button>
          </>
        )}
      </div>

      {activeTab === 'payer' && (
        <div className="dashboard-payer-filter">
          <label>Payer</label>
          <select
            value={selectedPayer}
            onChange={(e) => setSelectedPayer(e.target.value)}
          >
            <option value="">Select a payer...</option>
            {payers.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
      )}

      {activeTab === 'patient' && (
        <div className="dashboard-payer-filter">
          <label>Patient</label>
          <select
            value={selectedPatient}
            onChange={(e) => setSelectedPatient(e.target.value)}
          >
            <option value="">Select a patient...</option>
            {patients.map(p => (
              <option key={p.id} value={p.id}>{p.display_name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Patient tab rendering */}
      {activeTab === 'patient' ? (
        <>
          {patientError && (
            <div className="dashboard-error">
              <span>{patientError}</span>
              <button className="message-close" onClick={() => setPatientError(null)}>&times;</button>
            </div>
          )}
          {!selectedPatient ? (
            <div className="dashboard-empty">
              Select a patient to view their details.
            </div>
          ) : patientLoading ? (
            <div className="dashboard-loading">
              <div className="spinner"></div>
              <span>Loading patient data...</span>
            </div>
          ) : patientData ? (
            <>
              <div className="patient-top-row">
                {/* Demographics card */}
                <div className="dashboard-card patient-demographics">
                  <h3>Demographics</h3>
                  <div className="patient-details-grid">
                    <span className="detail-label">DOB</span>
                    <span>{(patientData.patient.patient_is_subscriber === 'Y' ? patientData.patient.subscriber_dob : patientData.patient.patient_dob) || ''}</span>
                    <span className="detail-label">Gender</span>
                    <span>{(patientData.patient.patient_is_subscriber === 'Y' ? patientData.patient.subscriber_gender : patientData.patient.patient_gender) || ''}</span>
                    <span className="detail-label">Phone</span>
                    <span>{patientData.patient.phone || ''}</span>
                    <span className="detail-label">Email</span>
                    <span>{patientData.patient.email || ''}</span>
                  </div>

                  {patientData.insurance_info.length > 0 && (
                    <div className="patient-insurance">
                      <h4>Insurance</h4>
                      <table className="insurance-table">
                        <thead>
                          <tr>
                            <th>Payer</th>
                            <th>Member ID</th>
                            <th>Claims</th>
                          </tr>
                        </thead>
                        <tbody>
                          {patientData.insurance_info.map((ins, i) => (
                            <tr key={i}>
                              <td>{ins.payer_name}</td>
                              <td>{ins.member_id || '-'}</td>
                              <td>{ins.claim_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                </div>

                {/* Claims Status chart */}
                <div className="dashboard-card patient-chart">
                  <h3>Claims Status</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={patientClaimsStatusData}>
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis hide domain={[0, (dataMax) => Math.ceil(dataMax * 1.15) || 1]} />
                      <Tooltip content={<CountTooltip />} />
                      <Legend />
                      <Bar dataKey="paid" name="Paid" fill={STATUS_COLORS.paid} stackId="a" />
                      <Bar dataKey="denied" name="Denied" fill={STATUS_COLORS.denied} stackId="a" />
                      <Bar dataKey="patient" name="Patient" fill={STATUS_COLORS.patient} stackId="a" />
                      <Bar dataKey="submitted" name="Submitted" fill={STATUS_COLORS.submitted} stackId="a" />
                      <Bar dataKey="appealing" name="Appealing" fill={STATUS_COLORS.appealing} stackId="a" />
                      <Bar dataKey="unsubmitted" name="Unsubmitted" fill={STATUS_COLORS.unsubmitted} stackId="a">
                        <LabelList dataKey="total" position="top" formatter={(v) => v > 0 ? `${v}` : ''} style={{ fontSize: 11, fill: '#1B2A4A', fontWeight: 500 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Mini claims ledger */}
              <div className="dashboard-card patient-claims-table">
                <h3>Claims ({patientData.claims.length})</h3>
                {patientData.claims.length === 0 ? (
                  <div className="patient-no-claims">No claims found for this patient.</div>
                ) : (
                  <div className="patient-claims-scroll">
                    <table className="mini-ledger">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Provider</th>
                          <th>Payer</th>
                          <th className="text-right">Billed</th>
                          <th className="text-right">Adj</th>
                          <th className="text-right">Total Paid</th>
                          <th className="text-right">Balance</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {patientData.claims.map(c => {
                          const billed = Number(c.billed_amount || 0);
                          const insPaid = Number(c.insurance_paid || 0);
                          const adj = Number(c.adjustment_amount || 0);
                          const ptPay = Number(c.patient_payment || 0);
                          const totalPaid = insPaid + ptPay;
                          const balance = billed - insPaid - adj - ptPay;
                          return (
                            <tr key={c.id}>
                              <td>{c.date_of_service}</td>
                              <td>{c.rendering_provider_name || '-'}</td>
                              <td>{c.payer_name || '-'}</td>
                              <td className="text-right">{formatCurrency(billed)}</td>
                              <td className="text-right">{formatCurrency(adj)}</td>
                              <td className="text-right">{formatCurrency(totalPaid)}</td>
                              <td className="text-right">{formatCurrency(balance)}</td>
                              <td>
                                <span className="status-badge" style={{ background: statusColor(c.claim_status) }}>
                                  {c.claim_status || 'draft'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </>
      ) : (
        /* General / Payer tab rendering */
        <>
          {activeError && (
            <div className="dashboard-error">
              <span>{activeError}</span>
              <button className="message-close" onClick={clearActiveError}>&times;</button>
            </div>
          )}

          {activeTab === 'payer' && !selectedPayer ? (
            <div className="dashboard-empty">
              Select a payer to view their dashboard.
            </div>
          ) : activeLoading ? (
            <div className="dashboard-loading">
              <div className="spinner"></div>
              <span>Loading dashboard...</span>
            </div>
          ) : activeData ? (
            <div className="dashboard-grid">
              {/* Top-left: Collected Payment */}
              <div className="dashboard-card">
                <h3>Collected Payment</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={collectedData}>
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis hide domain={[0, (dataMax) => Math.ceil(dataMax * 1.15) || 1]} />
                    <Tooltip content={<CurrencyTooltip />} />
                    <Bar dataKey="total" name="Total Collected" fill="#1B2A4A">
                      <LabelList dataKey="total" position="top" formatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(1)}K` : v > 0 ? `$${v.toFixed(0)}` : ''} style={{ fontSize: 11, fill: '#1B2A4A', fontWeight: 500 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Top-right: Payment Per Claim */}
              <div className="dashboard-card">
                <h3>Payment / Claim</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={paymentPerClaimData} margin={{ top: 20, right: 20, bottom: 5, left: 20 }}>
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} padding={{ left: 20, right: 20 }} />
                    <YAxis hide />
                    <Tooltip content={<CurrencyTooltip />} />
                    <Legend />
                    <Line type="monotone" dataKey="avg_all" name="All Claims" stroke="#1B2A4A" strokeWidth={2} dot={{ r: 3 }}>
                      <LabelList dataKey="avg_all" position="bottom" offset={10} formatter={(v) => v > 0 ? `$${Number(v).toFixed(0)}` : ''} style={{ fontSize: 10, fill: '#1B2A4A', fontWeight: 500 }} />
                    </Line>
                    <Line type="monotone" dataKey="avg_adjudicated" name="Adjudicated Claims" stroke="#C4973B" strokeWidth={2} dot={{ r: 3 }}>
                      <LabelList dataKey="avg_adjudicated" position="top" offset={10} formatter={(v) => v > 0 ? `$${Number(v).toFixed(0)}` : ''} style={{ fontSize: 10, fill: '#C4973B', fontWeight: 500 }} />
                    </Line>
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Bottom-left: Claims Status */}
              <div className="dashboard-card">
                <h3>Claims Status</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={claimsStatusData}>
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis hide domain={[0, (dataMax) => Math.ceil(dataMax * 1.15) || 1]} />
                    <Tooltip content={<CountTooltip />} />
                    <Legend />
                    <Bar dataKey="paid" name="Paid" fill={STATUS_COLORS.paid} stackId="a" />
                    <Bar dataKey="denied" name="Denied" fill={STATUS_COLORS.denied} stackId="a" />
                    <Bar dataKey="patient" name="Patient" fill={STATUS_COLORS.patient} stackId="a" />
                    <Bar dataKey="submitted" name="Submitted" fill={STATUS_COLORS.submitted} stackId="a" />
                    <Bar dataKey="appealing" name="Appealing" fill={STATUS_COLORS.appealing} stackId="a" />
                    <Bar dataKey="unsubmitted" name="Unsubmitted" fill={STATUS_COLORS.unsubmitted} stackId="a">
                      <LabelList dataKey="total" position="top" formatter={(v) => v > 0 ? `${v}` : ''} style={{ fontSize: 11, fill: '#1B2A4A', fontWeight: 500 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Bottom-right: Accounts Receivable */}
              <div className="dashboard-card">
                <h3>Accounts Receivable</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={arData}>
                    <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                    <YAxis hide domain={[0, (dataMax) => Math.ceil(dataMax * 1.15) || 1]} />
                    <Tooltip content={<CurrencyTooltip />} />
                    <Legend />
                    <Bar dataKey="insurer_owed" name="Insurer Owed" fill="#1B2A4A" stackId="a" />
                    <Bar dataKey="patient_owed" name="Patient Owed" fill="#C4973B" stackId="a">
                      <LabelList dataKey="total" position="top" formatter={(v) => v > 0 ? `${(v / 1000).toFixed(1)}K` : ''} style={{ fontSize: 11, fill: '#1B2A4A', fontWeight: 500 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default DashboardPage;
